import { Injectable } from '@angular/core';
import { TimeServiceProvider } from '@services/helpers/time-service/time.service';
import { MessageService } from '@services/helpers/message-service/message.service';
import { InvoicePayment } from '@models/models';

@Injectable()
export class PaymentsServiceProvider {
	constructor(
		private timeService: TimeServiceProvider,
		private messageService: MessageService
	) {
		this.handlePayment = this.handlePayment.bind(this);
	}

	isNegativePayments = (payments: Array<InvoicePayment>): boolean =>
		payments.some((payment): boolean => payment.amount < 0);

	isPaymentsWithoutDate = (payments: Array<InvoicePayment>): boolean =>
		payments.some((payment): boolean => !payment.due);

	isInvoicePaid = (payments: Array<InvoicePayment>): boolean =>
		payments.every(
			(payment): boolean => payment.paid === true || payment.writtenOff === true
		);

	isNonEditablePayments = (payments: Array<InvoicePayment>): boolean =>
		payments.some((payment): boolean => payment.editable !== true);

	isPaidPayments = (payments: Array<InvoicePayment>): boolean =>
		payments.some((payment): boolean => payment.paid);

	isWrittenOffPayments = (payments: Array<InvoicePayment>): boolean =>
		payments.some((payment): boolean => payment.writtenOff);

	setSentStatus(isSent): void {
		if (document.getElementById('stamp')) {
			document
				.getElementById('stamp')
				.setAttribute(
					'src',
					`https://app.studioninja.co/build/release-pipeline-51/resources/dist/assets/images/icons/statuses/${
						isSent ? 'sent.png' : 'notsent.png'
					}`
				);
		}
	}

	isValidDiscountPercent = (
		discountAmount: number,
		discountType: string,
		page: string
	): boolean => {
		if (discountType === 'Percent of order' && discountAmount > 100) {
			let errorCode, errorPlace;

			if (page === 'invoicePage') {
				errorCode = 'e_2';
				errorPlace = 'editInvoice';
			} else {
				errorCode = 'e_2';
				errorPlace = 'editQuote';
			}

			this.messageService.showToaster(errorCode, errorPlace);
			return false;
		}

		return true;
	};

	isXeroError = (packagesArray: Array<any>, discountType: string): boolean => {
		if (discountType === 'Fixed') {
			this.messageService.showToaster('e_1', 'xero');
			return true;
		}

		if (this.isMixedTaxTypes(packagesArray)) {
			this.messageService.showToaster('e_4', 'xero');
			return true;
		}

		return false;
	};

	isQuickbooksError = (
		packagesArray: Array<any>,
		discountType: string
	): boolean => {
		if (discountType === 'Fixed') {
			// $(document.body).animate({scrollTop: 0}, "slow");
			this.messageService.showToaster('e_1', 'xero');
			return true;
		}

		if (this.isMixedTaxTypes(packagesArray)) {
			// $(document.body).animate({scrollTop: 0}, "slow");
			this.messageService.showToaster('e_4', 'xero');
			return true;
		}

		// toastr.clear();
		return false;
	};

	isValidDiscountFixed = (
		packagesArray: Array<any>,
		discountType: string
	): boolean => {
		if (discountType === 'Fixed') {
			if (this.isMixedTaxTypes(packagesArray)) {
				this.messageService.showToaster('e_4', 'payments');
				return false;
			}
		}
		return true;
	};

	handlePayment(payment): InvoicePayment {
		const today = new Date();
		payment.days = this.timeService.calcDate(
			today,
			this.timeService.createDateAsUTC(payment.due)
		);

		if (payment.paidOn) {
			payment.paidOn = this.timeService.createDateAsUTC(payment.paidOn);
		}

		payment.late = false;

		let dayMulti = ' days';

		if (payment.days === 1 || payment.days === -1) {
			dayMulti = ' day';
		}

		if (payment.days === 0) {
			payment.days = 'today';
		} else if (payment.days > 0) {
			payment.days = `${payment.days + dayMulti} ago`;
			if (!payment.paid) {
				payment.late = true;
			}
		} else {
			payment.days = `in ${-payment.days}${dayMulti}`;
		}

		return payment;
	}

	setPaymentsDates = (payment: any) => {
		const today = new Date();

		let dateProperty = 'due';
		if (payment.hasOwnProperty('paymentDate')) {
			dateProperty = 'paymentDate';
		}

		payment.days = this.timeService.calcDate(
			today,
			this.timeService.createDateAsUTC(payment[dateProperty])
		);
		payment[dateProperty] = this.timeService.createDateAsUTC(
			payment[dateProperty]
		);
		payment.late = false;

		let dayMulti = ' days';
		if (payment.days === 1 || payment.days === -1) {
			dayMulti = ' day';
		}
		if (payment.days === 0) {
			payment.days = 'today';
		} else if (payment.days > 0) {
			payment.days = `${payment.days + dayMulti} ago`;
			payment.red = 'red';
			payment.late = true;
		} else {
			payment.days = `in ${-payment.days}${dayMulti}`;
		}

		return payment;
	};

	calcPrevFullTotal(products): number {
		let total = 0;
		products.forEach(product => {
			total += product.quantity * product.price;
		});
		return total;
	}

	createExistingPaymentObj(
		payment: InvoicePayment,
		isInvoice?
	): InvoicePayment {
		const isNonEditable = payment.paid || payment.writtenOff;
		return {
			amount: payment.amount,
			writtenOff: payment.writtenOff,
			paid: payment.paid,
			amountWithCurrency: payment.amountWithCurrency,
			due:
				payment.paymentDate === null && payment.due === null
					? null
					: this.timeService.createDateAsUTC(
							payment.paymentDate || payment.due
					  ),
			id: payment.id,
			editable: !isNonEditable,
			part: payment.part,
			schedule: isInvoice ? null : this.setPaymentScheduleRule(payment),
			paymentSchedulePart:
				payment.paymentSchedulePart === null
					? { amount: payment.amount }
					: payment.paymentSchedulePart
		};
	}

	setPaymentScheduleRule(payment): string {
		if (payment.paymentSchedulePart === null) {
			return '100% of order';
		}
		return `${payment.paymentSchedulePart.amount}${this.setAmountType(
			payment.paymentSchedulePart.type
		)}`;
	}

	setAmountType(type): string {
		switch (type) {
			case 'Percent of order':
				return '% of order';
			case 'Percent of remaining balance':
				return '% of remaining balance';
			default:
				return '';
		}
	}

	getNoneSchedule() {
		return {
			name: 'None',
			id: undefined
		};
	}

	private isMixedTaxTypes = (packagesArray: Array<any>): boolean => {
		const firstPackage = packagesArray[0] || {};
		const firstPackageTaxType = firstPackage.taxType || '';
		return packagesArray.some(
			(item): boolean => item.taxType !== firstPackageTaxType
		);
	};
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=gh4osmq0bhk!./shared/services/payments/payments-service/payments.service.ts